import { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { CheckCircle2, XCircle, Loader2, ArrowRight, Lock, User, Mail } from "lucide-react";

const LOGO_DARK = "https://kargonode.com/wp-content/uploads/2026/02/logo-correo.webp";
const LOGO_LIGHT = "https://kargonode.com/wp-content/uploads/2026/03/Manual-de-Marca-HUMAN-CLARITY-Pilar-Rodriguez-9.webp";

function useIsDark() {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains("dark"));
  useEffect(() => {
    const observer = new MutationObserver(() => setIsDark(document.documentElement.classList.contains("dark")));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => observer.disconnect();
  }, []);
  return isDark;
}

interface InviteData {
  valid: boolean;
  reason?: string;
  org_name?: string;
  email?: string;
  rol?: string;
  existe_usuario?: boolean;
}

const Onboarding = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isDark = useIsDark();
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!token) {
      setInviteData({ valid: false, reason: "No se proporcionó un token de invitación." });
      setLoading(false);
      return;
    }
    const validate = async () => {
      try {
        const { data, error } = await supabase.rpc("validar_invitacion_publica", { _token: token });
        if (error) throw error;
        const invite = data as unknown as InviteData;

        // Defense-in-depth: if there's an active session for a DIFFERENT email, sign out first
        if (invite.valid && invite.email) {
          const { data: sessionData } = await supabase.auth.getSession();
          const currentEmail = sessionData?.session?.user?.email;
          if (currentEmail && currentEmail.toLowerCase() !== invite.email.toLowerCase()) {
            await supabase.auth.signOut();
          }
        }

        setInviteData(invite);
      } catch {
        setInviteData({ valid: false, reason: "Error al validar la invitación." });
      }
      setLoading(false);
    };
    validate();
  }, [token]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!inviteData?.existe_usuario) {
      if (password.length < 6) {
        setError("La contraseña debe tener al menos 6 caracteres.");
        return;
      }
      if (password !== confirmPassword) {
        setError("Las contraseñas no coinciden.");
        return;
      }
    }

    setSubmitting(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke("process-invitation", {
        body: {
          token,
          password: inviteData?.existe_usuario ? undefined : password,
          full_name: fullName || undefined,
        },
      });

      if (fnError) throw fnError;
      if (data?.error) {
        setError(data.error);
        setSubmitting(false);
        return;
      }

      setSuccess(true);
      setTimeout(() => navigate("/login", { state: { message: "Tu cuenta ha sido activada. Bienvenido a bordo." } }), 3500);
    } catch (err: any) {
      setError(err.message || "Error al procesar la invitación.");
    }
    setSubmitting(false);
  };

  const logo = isDark ? LOGO_DARK : LOGO_LIGHT;

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Top bar with logo */}
      <header className="w-full px-6 py-4 md:px-10 md:py-5">
        <img src={logo} alt="KargoNode" className="h-8 md:h-9 object-contain" />
      </header>

      {/* Center content */}
      <main className="flex-1 flex items-center justify-center px-4 pb-12">
        <div className="w-full max-w-md">
          {loading ? (
            <LoadingState />
          ) : success ? (
            <SuccessState />
          ) : !inviteData?.valid ? (
            <InvalidState reason={inviteData?.reason} onGoLogin={() => navigate("/login")} />
          ) : (
            <OnboardingForm
              inviteData={inviteData}
              fullName={fullName}
              setFullName={setFullName}
              password={password}
              setPassword={setPassword}
              confirmPassword={confirmPassword}
              setConfirmPassword={setConfirmPassword}
              error={error}
              submitting={submitting}
              onSubmit={handleSubmit}
            />
          )}
        </div>
      </main>

      {/* Footer */}
      <footer className="w-full text-center py-4 text-xs text-muted-foreground">
        © {new Date().getFullYear()} KargoNode — Powered by Human Clarity
      </footer>
    </div>
  );
};

/* ─── Sub-components ─── */

function LoadingState() {
  return (
    <div className="flex flex-col items-center gap-4 py-16">
      <Loader2 className="h-10 w-10 animate-spin text-primary" />
      <p className="text-muted-foreground text-sm">Validando invitación…</p>
    </div>
  );
}

function SuccessState() {
  return (
    <Card className="border-border overflow-hidden">
      <CardContent className="pt-10 pb-10 flex flex-col items-center gap-5">
        <div className="relative">
          <div className="absolute inset-0 rounded-full bg-primary/20 animate-ping" />
          <div className="relative p-4 rounded-full bg-primary/10">
            <CheckCircle2 className="h-12 w-12 text-primary" />
          </div>
        </div>
        <div className="text-center space-y-2">
          <h2 className="text-xl font-semibold text-foreground">¡Cuenta activada!</h2>
          <p className="text-sm text-muted-foreground max-w-xs">
            Tu cuenta ha sido configurada exitosamente. Te estamos redirigiendo al inicio de sesión…
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground mt-2">
          <Loader2 className="h-3 w-3 animate-spin" />
          Redirigiendo…
        </div>
      </CardContent>
    </Card>
  );
}

function InvalidState({ reason, onGoLogin }: { reason?: string; onGoLogin: () => void }) {
  return (
    <Card className="border-border overflow-hidden">
      <CardContent className="pt-10 pb-10 flex flex-col items-center gap-5">
        <div className="p-4 rounded-full bg-destructive/10">
          <XCircle className="h-12 w-12 text-destructive" />
        </div>
        <div className="text-center space-y-2">
          <h2 className="text-xl font-semibold text-foreground">Enlace Inválido</h2>
          <p className="text-sm text-muted-foreground max-w-xs">
            {reason || "El enlace de invitación no es válido o ha expirado."}
          </p>
        </div>
        <Button variant="outline" onClick={onGoLogin} className="gap-2">
          Ir al Inicio de Sesión
          <ArrowRight className="h-4 w-4" />
        </Button>
      </CardContent>
    </Card>
  );
}

interface OnboardingFormProps {
  inviteData: InviteData;
  fullName: string;
  setFullName: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  confirmPassword: string;
  setConfirmPassword: (v: string) => void;
  error: string;
  submitting: boolean;
  onSubmit: (e: React.FormEvent) => void;
}

function OnboardingForm({
  inviteData,
  fullName,
  setFullName,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  error,
  submitting,
  onSubmit,
}: OnboardingFormProps) {
  return (
    <div className="space-y-6">
      {/* Header text */}
      <div className="text-center space-y-2">
        <h1 className="text-2xl font-bold text-foreground tracking-tight">
          {inviteData.existe_usuario
            ? `Únete a ${inviteData.org_name}`
            : "Configura tu acceso"}
        </h1>
        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
          {inviteData.existe_usuario
            ? `Confirma tu incorporación a ${inviteData.org_name} con tu cuenta existente.`
            : `Has sido invitado a ${inviteData.org_name}. Crea tu cuenta para comenzar.`}
        </p>
      </div>

      {/* Form card */}
      <Card className="border-border">
        <CardContent className="p-6 md:p-8">
          <form onSubmit={onSubmit} className="space-y-5">
            {/* Email (read-only) */}
            <div className="space-y-1.5">
              <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Email
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  value={inviteData.email || ""}
                  disabled
                  className="pl-10 bg-muted/50 text-foreground"
                />
              </div>
            </div>

            {!inviteData.existe_usuario && (
              <>
                {/* Full Name */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Nombre completo
                  </Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Tu nombre completo"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Password */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Contraseña
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="password"
                      placeholder="Mínimo 6 caracteres"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="pl-10"
                    />
                  </div>
                </div>

                {/* Confirm Password */}
                <div className="space-y-1.5">
                  <Label className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Confirmar contraseña
                  </Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      type="password"
                      placeholder="Repite tu contraseña"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      required
                      minLength={6}
                      className="pl-10"
                    />
                  </div>
                </div>
              </>
            )}

            {error && (
              <p className="text-sm text-destructive bg-destructive/10 rounded-lg px-4 py-2.5 border border-destructive/20">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={submitting}
              className="w-full h-11 gap-2 text-sm font-semibold"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Procesando…
                </>
              ) : inviteData.existe_usuario ? (
                <>
                  Confirmar Unión
                  <ArrowRight className="h-4 w-4" />
                </>
              ) : (
                <>
                  Crear Cuenta
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Trust badges */}
      <div className="flex items-center justify-center gap-4 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <Lock className="h-3 w-3" />
          Conexión segura
        </span>
        <span>•</span>
        <span>Datos encriptados</span>
      </div>
    </div>
  );
}

export default Onboarding;
